/**
 * The Bexio invoice → tenancy mapping modal.
 *
 * MOVED VERBATIM out of `AdminBexioPage.js` (UI-4b), for the same reason as the
 * contact modal beside it: the legacy page, the new invoices page and the invoice
 * drawer must share one mapping implementation, including its 409 handling.
 *
 * Unchanged in the move: the tenancy list is loaded from `fetchAdminTenanciesAll`
 * with no preselection, a 409 reloads the single mapping status and routes into
 * the correct flow instead of guessing, and the server's message survives to the
 * toast.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  createBexioInvoiceMapping,
  deleteBexioInvoiceMapping,
  fetchBexioInvoiceMapping,
  updateBexioInvoiceMapping,
} from "../../../../api/adminData/bexio";
import { fetchAdminTenanciesAll } from "../../../../api/adminData/tenancies";
import Badge from "./BexioBadge";
import {
  INVOICE_MAPPING_STATUS_LABEL,
  fmtDateCH,
  fmtInvoiceMoney,
  invoiceMappingTone,
  tenancyPrimaryName,
} from "../bexioFormat";

/* Moved with this modal: the page no longer references it. */

function tenancyLabel(t) {
  const parts = [];
  const name = tenancyPrimaryName(t);
  if (name) parts.push(name);
  if (t?.reference_number) parts.push(t.reference_number);
  const place = [t?.unit_short_unit_id, t?.room_name].filter(Boolean).join(" · ");
  if (place) parts.push(place);
  // No raw (status) suffix: the stored tenancy.status (e.g. "reserved" for a
  // moved-in tenancy) is misleading here and not needed to identify the tenancy.
  // Do NOT derive an operational/lifecycle status in this picker.
  return parts.join(" · ") || t?.id;
}

// ─── B1c.2d: manual Bexio invoice → Tenancy mapping modal (admin only) ──

export default function BexioInvoiceMappingModal({ invoice, onClose, onChanged }) {
  const [currentStatus, setCurrentStatus] = useState(null);
  const [tenancies, setTenancies] = useState([]);
  const [selectedTenancyId, setSelectedTenancyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // conflict: null | { mode: "confirm_change" | "repair", message }
  const [conflict, setConflict] = useState(null);

  const applyStatus = useCallback((st, opts) => {
    setCurrentStatus(st);
    if (opts && opts.preselect) {
      // ONLY an existing valid mapping shows its current tenancy. unmapped/invalid
      // never preselect (no auto-suggestion).
      setSelectedTenancyId(st?.mapping_status === "mapped" ? st.tenancy_id || "" : "");
    }
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    Promise.all([
      fetchBexioInvoiceMapping(invoice.id),
      fetchAdminTenanciesAll({ include_cancelled: true }),
    ])
      .then(([st, tens]) => {
        if (!alive) return;
        applyStatus(st, { preselect: true });
        setTenancies(Array.isArray(tens) ? tens : []);
      })
      .catch((e) => {
        if (alive) setError(e?.message || "Mapping konnte nicht geladen werden.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [invoice.id, applyStatus]);

  const reloadStatus = useCallback(async () => {
    const st = await fetchBexioInvoiceMapping(invoice.id);
    applyStatus(st);
    return st;
  }, [invoice.id, applyStatus]);

  // After a 409: reload the single status and route into the correct flow.
  const routeAfterConflict = useCallback(async () => {
    const st = await reloadStatus();
    if (st?.mapping_status === "mapped") {
      setConflict({
        mode: "confirm_change",
        message:
          "Diese Rechnung ist bereits einer anderen Tenancy zugeordnet. Bitte Änderung bestätigen.",
      });
    } else if (st?.mapping_status === "invalid") {
      setConflict({
        mode: "repair",
        message:
          "Das aktuelle Mapping ist ungültig/inkonsistent und muss zuerst entfernt werden.",
      });
    } else {
      setError("Zuordnung konnte nicht gespeichert werden. Bitte erneut prüfen.");
    }
  }, [reloadStatus]);

  const status = currentStatus?.mapping_status || "unmapped";

  const handleSave = async () => {
    if (busy || !selectedTenancyId || status === "invalid") return;
    setBusy(true);
    setError("");
    try {
      if (status === "mapped") {
        await updateBexioInvoiceMapping(invoice.id, selectedTenancyId);
      } else {
        await createBexioInvoiceMapping(invoice.id, selectedTenancyId);
      }
      toast.success("Zuordnung gespeichert.");
      await reloadStatus();
      await onChanged();
    } catch (e) {
      if (e?.status === 409) {
        await routeAfterConflict();
      } else {
        toast.error(e?.message || "Zuordnung fehlgeschlagen.");
        setError(e?.message || "Zuordnung fehlgeschlagen.");
      }
    } finally {
      setBusy(false);
    }
  };

  // "Trotzdem ändern" after a confirm_change conflict → PATCH retarget.
  const handleForceChange = async () => {
    if (busy || !selectedTenancyId) return;
    setBusy(true);
    setError("");
    try {
      await updateBexioInvoiceMapping(invoice.id, selectedTenancyId);
      toast.success("Zuordnung geändert.");
      setConflict(null);
      await reloadStatus();
      await onChanged();
    } catch (e) {
      if (e?.status === 409) {
        await routeAfterConflict();
      } else {
        toast.error(e?.message || "Änderung fehlgeschlagen.");
      }
    } finally {
      setBusy(false);
    }
  };

  // Repair: remove the invalid/partial mapping, then admin can map fresh.
  const handleRepair = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await deleteBexioInvoiceMapping(invoice.id);
      toast.success("Ungültiges Mapping entfernt.");
      setConflict(null);
      setSelectedTenancyId("");
      await reloadStatus();
      await onChanged();
    } catch (e) {
      toast.error(e?.message || "Entfernen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const selectedTenancy = tenancies.find((t) => t.id === selectedTenancyId) || null;
  const showRepair = status === "invalid" || conflict?.mode === "repair";
  const showConfirmChange = conflict?.mode === "confirm_change";
  const canSave = !!selectedTenancyId && status !== "invalid" && !showConfirmChange;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[85vh] w-full max-w-[720px] overflow-y-auto rounded-[12px] border border-border bg-card p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[16px] font-bold text-foreground">
              Bexio-Rechnung zuordnen
            </h2>
            <div className="mt-1 text-[12px] text-foreground">
              {invoice.document_nr || "—"}
              {invoice.title ? ` · ${invoice.title}` : ""}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {invoice.contact_name ? `${invoice.contact_name} · ` : ""}
              {fmtDateCH(invoice.issue_date)} · {fmtInvoiceMoney(invoice.total, invoice.currency)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-[13px] text-muted-foreground">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-input border-t-primary" />
            Mapping wird geladen …
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-2 text-[12px]">
              <span className="text-muted-foreground">Aktueller Status:</span>
              <Badge tone={invoiceMappingTone(status)}>
                {INVOICE_MAPPING_STATUS_LABEL[status] || status}
              </Badge>
              {status === "mapped" && currentStatus?.tenancy_reference ? (
                <span className="text-[11px] text-foreground">
                  {currentStatus.tenancy_reference}
                  {currentStatus.tenant_display_name
                    ? ` · ${currentStatus.tenant_display_name}`
                    : ""}
                </span>
              ) : null}
            </div>

            {showRepair ? (
              <div className="mb-3 rounded-[8px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-[12px] text-red-700 dark:text-red-300">
                <div>
                  {conflict?.message ||
                    "Aktuelles Mapping ist ungültig/inkonsistent."}
                </div>
                <button
                  type="button"
                  onClick={handleRepair}
                  disabled={busy}
                  className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20 disabled:opacity-60"
                >
                  Ungültiges Mapping entfernen
                </button>
              </div>
            ) : null}

            {showConfirmChange ? (
              <div className="mb-3 rounded-[8px] border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-[12px] text-amber-800 dark:text-amber-300">
                <div>{conflict.message}</div>
                <button
                  type="button"
                  onClick={handleForceChange}
                  disabled={busy || !selectedTenancyId}
                  className="mt-2 rounded-[8px] border border-amber-200 dark:border-amber-500/30 px-3 py-1 text-[12px] text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20 disabled:opacity-60"
                >
                  Trotzdem ändern
                </button>
              </div>
            ) : null}

            <label className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
              Tenancy (inkl. beendete/gekündigte)
            </label>
            <select
              aria-label="Tenancy wählen"
              value={selectedTenancyId}
              disabled={status === "invalid"}
              onChange={(e) => setSelectedTenancyId(e.target.value)}
              className="w-full rounded-[8px] border border-border bg-card px-3 py-2 text-[13px] text-foreground disabled:opacity-50"
            >
              <option value="">— Tenancy wählen —</option>
              {tenancies.map((t) => (
                <option key={t.id} value={t.id}>
                  {tenancyLabel(t)}
                </option>
              ))}
            </select>

            {selectedTenancy ? (
              <div className="mt-2 rounded-[8px] border border-border bg-card p-3 text-[11px] text-foreground">
                <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  Gewählte Tenancy (nur Anzeige)
                </div>
                <div className="mt-1 text-[13px] text-foreground">
                  {tenancyPrimaryName(selectedTenancy) || "—"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {selectedTenancy.reference_number || "—"}
                  {selectedTenancy.status ? ` · ${selectedTenancy.status}` : ""}
                  {selectedTenancy.unit_short_unit_id
                    ? ` · ${selectedTenancy.unit_short_unit_id}`
                    : ""}
                  {selectedTenancy.room_name ? ` · ${selectedTenancy.room_name}` : ""}
                </div>
              </div>
            ) : null}

            {error ? (
              <div className="mt-3 text-[12px] text-red-700 dark:text-red-300">{error}</div>
            ) : null}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-[8px] border border-input px-3 py-1.5 text-[12px] text-foreground hover:bg-muted"
              >
                Schließen
              </button>
              {status !== "invalid" && !showConfirmChange ? (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave || busy}
                  className="rounded-[8px] bg-primary px-4 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {status === "mapped" ? "Zuordnung ändern" : "Zuordnung speichern"}
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
